import { Grid, List, ListItem, ListItemButton, ListItemText, TextField } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { config } from "../../config";
import { SelectOption } from "../../types/SelectOption";

export interface TextInputProps {
  name: string;

  suggestions?: {
    initDispVal: string;
    options: SelectOption[];
  },

  value: string | number | undefined;
  label: string | undefined;
  align?: 'left' | 'center' | 'right'
  paddingRight?: number;
  error?: boolean;
  isRequired?: boolean;
  type?: string;
  multiline?: boolean;
  disabled?: boolean;

  onChange: (key: string, value: string | number, fromSuggestions?: boolean) => void;
  onEnter?: () => void;
}
export const TextInput: FC<TextInputProps> = ({
  name,
  suggestions,
  value = '',
  error,
  label,
  isRequired,
  onChange,
  disabled,
  multiline,
  type,
  paddingRight = 0,
  align = 'left',
  onEnter
}) => {

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [placeHolder, setPlaceHolder] = useState(label);

  const [currentSuggestions, setCurrentSuggestions] = useState<SelectOption[] | undefined>([]);

  useEffect(() => {
    initValues(value, false);
  }, [value]);

  useEffect(() => {
    setCurrentSuggestions(suggestions?.options);
    if (JSON.stringify(currentSuggestions) !== JSON.stringify(suggestions?.options)) {
      initValues(value, true);
    }
  }, [suggestions?.options]);

  /**
   * 
   * @param currentValueFromProps local value at this component which came from props
   * @param informParent 
   */
  const initValues = (currentValueFromProps: string | number, informParent: boolean) => {

    if (suggestions) {
      // This is an autocomplete textbox
      const valFromOptions = suggestions.options.find(o => {
        const numVal = Number(currentValueFromProps);
        if (isNaN(numVal)) {
          return o.label.toLowerCase() === `${currentValueFromProps}`.toLowerCase();
        } else {
          return o.id === numVal;
        }
      });
      if (valFromOptions) {
        setInputValue(valFromOptions.label);
        setPlaceHolder(label);
        if (informParent) {
          onChange(name, valFromOptions.id, true);
        }
      } else {
        setPlaceHolder(`${label}(New)`);
        if (suggestions.initDispVal) {
          setInputValue(suggestions.initDispVal);
        }

        if (informParent) {
          const val = (currentValueFromProps === -1) ?
            suggestions.initDispVal // Most of the cases initDispVal comes from extraProps
            : currentValueFromProps;
          onChange(name, val, false);
        }
      }
    } else {
      // This is normal input box
      setInputValue(`${currentValueFromProps}`);
      if (informParent) {
        onChange(name, currentValueFromProps ?? '', false);
      }
    }
  }

  const handleKeyUp = (e: any) => {
    if (onEnter) {
      if (e.key === 'Enter') {
        onEnter();
      }
    } else {
      processInput(e.target.value);
    }
  }

  const processInput = (inputVal: string) => {
    const valFromOptions = suggestions?.options.find(o => o.label.toLowerCase() === inputVal.toLowerCase());
    if (valFromOptions) {
      onChange(name, valFromOptions.id, true);
      setInputValue(valFromOptions.label);
      setPlaceHolder(label);
    } else {
      onChange(name, inputVal);
      setInputValue(inputVal);
      setPlaceHolder(`${label} (New)`);
    }

    if (filteredSuggestions?.length !== 0) {
      setShowSuggestions(true);
    }
  }

  const handleFocusChange = (showSuggestions: boolean, e: any) => {
    setTimeout(() => {
      setShowSuggestions(showSuggestions);
    }, 500);
    processInput(e.target.value)
  }

  const handleSelectSuggestion = (key: string, val: string | number, label: string) => {
    setInputValue(label);
    setPlaceHolder(label);
    onChange(name, val, true);
    // suggestions?.onSelect?.(key, val);
  }

  const handleInputChange = (key: string, val: string) => {
    setInputValue(val);
    processInput(val)
  }

  const filteredSuggestions = useMemo(() => {
    const filt = suggestions?.options.filter(opt => opt.label.toLowerCase().includes(`${inputValue}`.toLowerCase())).slice(0, 10);
    return filt;
  }, [suggestions?.options, inputValue]);

  return (
    <Grid>
      <TextField
        onFocus={(e) => handleFocusChange(true, e)}
        onBlur={(e) => handleFocusChange(false, e)}
        value={inputValue}
        onChange={(e) => handleInputChange(name, e.target.value)}
        onKeyUp={handleKeyUp}
        required={isRequired}
        fullWidth
        type={type}
        multiline={multiline}
        rows={multiline ? 4 : 1}
        label={placeHolder}
        autoComplete="off"
        error={error}
        disabled={disabled}
        variant="standard"
        InputProps={{
          sx: {
            "& input": {
              textAlign: align
            },
            paddingRight
          },
        }}
        inputProps={{ maxLength: multiline ? config.multilineTextLimit : config.inputTextLimit }}
      />
      {showSuggestions &&
        <List sx={{
          maxHeight: '20vh', overflow: 'auto',
          background: 'white'
        }}>
          {
            filteredSuggestions?.map(item => <ListItem key={item.id} disablePadding>
              <ListItemButton onClick={() => handleSelectSuggestion(name, item.id, item.label)}>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>)
          }
        </List>}
    </Grid>
  );
};
